import { MetaType } from 'shared/ts-types/meta-type';
import { createReducer } from '@reduxjs/toolkit';
import {
  GcashActionLogResponse,
  GcashApplicationResponse,
  GcashApplicationDocumentResponse,
  GcashDocumentByStatusResponse,
  GcashDocumentsStaticticsResponse,
  GcashDocumentTypeItem,
  GcashPageResponse,
} from 'features/gcash/api/types';
import { gcashActions } from 'features/gcash/redux/gcash.actions';
import { GcashDocumentStatuses } from 'features/gcash/enums/gcash-document-statuses.enum';
import { GcashDocumentTypes } from 'features/gcash/enums/gcash-document-types.enum';

const initialState = {
  statistics: {
    items: {} as GcashDocumentsStaticticsResponse,
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getStatistics: [] as string[],
    },
  },
  limitCounterStatistics: {
    documentCount: 0,
    limit: 0,
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getLimitCounterStatistics: [] as string[],
    },
  },
  globalLoading: false as boolean,
  documentsStatusChecking: false as boolean,
  logs: {
    items: [] as GcashActionLogResponse[],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 1,
    } as MetaType,
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getLogs: [] as string[],
    },
  },
  applications: {
    items: [] as GcashApplicationResponse[],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 1,
    } as MetaType,
    loading: false as boolean,
    isLoaded: false as boolean,
    selectedApplication: null as GcashApplicationResponse | null,
    error: {
      getApplications: [] as string[],
      getApplication: [] as string[],
    },
  },
  createApplication: {
    loading: false as boolean,
    error: [] as string[],
    isSubmitted: false as boolean,
  },
  documents: {
    items: [] as GcashApplicationDocumentResponse[],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 1,
    } as MetaType,
    selectedDocument: null as GcashApplicationDocumentResponse | null,
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getDocuments: [] as string[],
      getDocument: [] as string[],
    },
  },
  createDocument: {
    loading: false as boolean,
    error: [] as string[],
    isSubmitted: false as boolean,
  },
  changeDocumentStatus: {
    loading: false as boolean,
    error: [] as string[],
    isSubmitted: false as boolean,
  },
  pages: {
    items: [] as GcashPageResponse[],
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getPages: [] as string[],
    },
  },
  createPage: {
    loading: false as boolean,
    error: [] as string[],
    isSubmitted: false as boolean,
  },
  documentsByStatus: {
    items: [] as GcashDocumentByStatusResponse[],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 1,
    } as MetaType,
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      getDocumentsByStatus: [] as string[],
    },
  },
  exportDocuments: {
    loading: false as boolean,
    isLoaded: false as boolean,
    error: {
      exportDocument: [] as string[],
      exportSingleDocument: [] as string[],
      exportApplication: [] as string[],
    },
  },
  documentTypes: {
    items: [] as GcashDocumentTypeItem[],
    loading: false as boolean,
    isLoaded: false as boolean,
    error: [] as string[],
  },
};

const gcash = createReducer(initialState, (builder) => {
  builder.addCase(gcashActions.clearState, (state) => {
    return {
      ...initialState,
      limitCounterStatistics: {
        ...state.limitCounterStatistics,
      },
    };
  });
  builder.addCase(gcashActions.getGcashStatisticsRequest, (state) => {
    return {
      ...state,
      statistics: {
        ...state.statistics,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.getGcashStatisticsSuccess, (state, action) => {
    return {
      ...state,
      statistics: {
        items: action.payload,
        loading: false,
        isLoaded: true,
        error: {
          ...state.statistics.error,
          getStatistics: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.getGcashStatisticsFailure, (state, action) => {
    return {
      ...state,
      statistics: {
        ...state.statistics,
        loading: false,
        isLoaded: false,
        error: {
          getStatistics: action.payload,
        },
      },
    };
  });

  builder.addCase(gcashActions.getGcashLogsRequest, (state) => {
    return {
      ...state,
      logs: {
        ...state.logs,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.getGcashLogsSuccess, (state, action) => {
    return {
      ...state,
      logs: {
        items: [...state.logs.items, ...action.payload.items],
        meta: action.payload.meta,
        loading: false,
        isLoaded: true,
        error: {
          ...state.logs.error,
          getLogs: [],
        },
      },
    };
  });

  builder.addCase(gcashActions.getGcashLogsFailure, (state, action) => {
    return {
      ...state,
      logs: {
        ...state.logs,
        loading: false,
        isLoaded: false,
        error: {
          getLogs: action.payload,
        },
      },
    };
  });

  builder.addCase(gcashActions.getGcashApplicationsRequest, (state) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        loading: true,
      },
    };
  });

  builder.addCase(gcashActions.getGcashApplicationsSuccess, (state, action) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        items:
          action.payload.meta.currentPage === 1
            ? [...action.payload.items]
            : [...state.applications.items, ...action.payload.items],
        meta: action.payload.meta,
        loading: false,
        isLoaded: true,
        error: {
          ...state.applications.error,
          getApplications: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.getGcashApplicationsFailure, (state, action) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        loading: false,
        isLoaded: false,
        error: {
          ...state.applications.error,
          getApplications: action.payload,
        },
      },
    };
  });
  builder.addCase(gcashActions.createGcashApplicationRequest, (state) => {
    return {
      ...state,
      createApplication: {
        ...state.createApplication,
        loading: true,
        isSubmitted: false,
      },
    };
  });
  builder.addCase(
    gcashActions.createGcashApplicationSuccess,
    (state, action) => {
      return {
        ...state,
        createApplication: {
          loading: false,
          error: [],
          isSubmitted: true,
        },
        applications: {
          ...state.applications,
          items: [action.payload, ...state.applications.items],
        },
      };
    }
  );
  builder.addCase(
    gcashActions.createGcashApplicationFailure,
    (state, action) => {
      return {
        ...state,
        createApplication: {
          loading: false,
          error: action.payload,
          isSubmitted: false,
        },
      };
    }
  );
  builder.addCase(gcashActions.getGcashApplicationRequest, (state) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.getGcashApplicationSuccess, (state, action) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        selectedApplication: action.payload,
        loading: false,
        isLoaded: true,
        error: {
          ...state.applications.error,
          getApplication: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.getGcashApplicationFailure, (state, action) => {
    return {
      ...state,
      applications: {
        ...state.applications,
        loading: false,
        isLoaded: false,
        error: {
          ...state.applications.error,
          getApplication: action.payload,
        },
      },
    };
  });
  builder.addCase(gcashActions.getGcashApplicationDocumentsRequest, (state) => {
    return {
      ...state,
      documents: {
        ...state.documents,
        loading: true,
      },
    };
  });
  builder.addCase(
    gcashActions.getGcashApplicationDocumentsSuccess,
    (state, action) => {
      return {
        ...state,
        documents: {
          ...state.documents,
          items:
            action.payload.meta.currentPage === 1
              ? [...action.payload.items]
              : [...state.documents.items, ...action.payload.items],
          meta: action.payload.meta,
          loading: false,
          isLoaded: true,
          error: {
            ...state.documents.error,
            getDocuments: [],
          },
        },
      };
    }
  );
  builder.addCase(
    gcashActions.getGcashApplicationDocumentsFailure,
    (state, action) => {
      return {
        ...state,
        documents: {
          ...state.documents,
          loading: false,
          isLoaded: false,
          error: {
            ...state.documents.error,
            getDocuments: action.payload,
          },
        },
      };
    }
  );
  builder.addCase(
    gcashActions.createGcashApplicationDocumentRequest,
    (state, action) => {
      return {
        ...state,
        createDocument: {
          ...state.createDocument,
          loading: true,
          isSubmitted: false,
        },
        globalLoading: true,
        documents: {
          ...state.documents,
          items: [
            {
              id: '0',
              documentName: action.payload.documentName,
              type: action.payload.type as GcashDocumentTypes,
              status: GcashDocumentStatuses.PROCESSING,
              updatedAt: new Date().toString(),
              createdAt: new Date().toString(),
            },
            ...state.documents.items,
          ],
        },
      };
    }
  );
  builder.addCase(
    gcashActions.createGcashApplicationDocumentSuccess,
    (state, action) => {
      return {
        ...state,
        globalLoading: false,
        createDocument: {
          ...state.createDocument,
          loading: false,
          error: [],
          isSubmitted: true,
        },
        documents: {
          ...state.documents,
          items: [action.payload, ...state.documents.items.slice(1)],
        },
      };
    }
  );
  builder.addCase(
    gcashActions.createGcashApplicationDocumentFailure,
    (state, action) => {
      return {
        ...state,
        globalLoading: false,
        createDocument: {
          loading: false,
          error: action.payload,
          isSubmitted: false,
        },
        documents: {
          ...state.documents,
          items: [...state.documents.items.slice(1)],
        },
      };
    }
  );
  builder.addCase(gcashActions.getGcashDocumentInfoRequest, (state) => {
    return {
      ...state,
      documents: {
        ...state.documents,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.getGcashDocumentInfoSuccess, (state, action) => {
    return {
      ...state,
      documents: {
        ...state.documents,
        selectedDocument: action.payload,
        loading: false,
        isLoaded: true,
        error: {
          ...state.documents.error,
          getDocument: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.getGcashDocumentInfoFailure, (state, action) => {
    return {
      ...state,
      documents: {
        ...state.documents,
        loading: false,
        isLoaded: false,
        error: {
          ...state.documents.error,
          getDocument: action.payload,
        },
      },
    };
  });
  builder.addCase(gcashActions.createGcashNewPageRequest, (state) => {
    return {
      ...state,
      globalLoading: true,
      createPage: {
        ...state.createPage,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.createGcashNewPageSuccess, (state, action) => {
    return {
      ...state,
      pages: {
        ...state.pages,
        items: [...state.pages.items, ...action.payload.items],
      },
      globalLoading: false,
      createPage: {
        ...state.createPage,
        loading: false,
        isSubmitted: true,
        error: [],
      },
    };
  });
  builder.addCase(gcashActions.createGcashNewPageFailure, (state, action) => {
    return {
      ...state,
      globalLoading: false,
      createPage: {
        ...state.createPage,
        loading: false,
        isLoaded: false,
        error: action.payload,
      },
    };
  });
  (builder.addCase(gcashActions.changeGcashDocumentStatusRequest, (state) => {
    return {
      ...state,
      changeDocumentStatus: {
        ...state.changeDocumentStatus,
        loading: true,
        isSubmitted: false,
      },
    };
  }),
    builder.addCase(
      gcashActions.changeGcashDocumentStatusSuccess,
      (state, action) => {
        return {
          ...state,
          changeDocumentStatus: {
            ...state.changeDocumentStatus,
            loading: false,
            isSubmitted: true,
            error: [],
          },
          documents: {
            ...state.documents,
            selectedDocument: action.payload,
          },
        };
      }
    ),
    builder.addCase(
      gcashActions.changeGcashDocumentStatusFailure,
      (state, action) => {
        return {
          ...state,
          changeDocumentStatus: {
            ...state.changeDocumentStatus,
            loading: false,
            isSubmitted: false,
            error: action.payload,
          },
        };
      }
    ));
  builder.addCase(gcashActions.getGcashDocumentPagesRequest, (state) => {
    return {
      ...state,
      pages: {
        ...state.pages,
        loading: true,
      },
    };
  });
  builder.addCase(
    gcashActions.getGcashDocumentPagesSuccess,
    (state, action) => {
      return {
        ...state,
        pages: {
          ...state.pages,
          items: [...action.payload.items],
          loading: false,
          isLoaded: true,
          error: {
            ...state.pages.error,
            getPages: [],
          },
        },
      };
    }
  );
  builder.addCase(
    gcashActions.getGcashDocumentPagesFailure,
    (state, action) => {
      return {
        ...state,
        pages: {
          ...state.pages,
          loading: false,
          isLoaded: false,
          error: {
            ...state.pages.error,
            getPages: action.payload,
          },
        },
      };
    }
  );
  builder.addCase(gcashActions.getDocumentsByStatusRequest, (state) => {
    return {
      ...state,
      documentsByStatus: {
        ...state.documentsByStatus,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.getDocumentsByStatusSuccess, (state, action) => {
    return {
      ...state,
      documentsByStatus: {
        ...state.documentsByStatus,
        items:
          action.payload.meta.currentPage === 1
            ? [...action.payload.items]
            : [...state.documentsByStatus.items, ...action.payload.items],
        meta: action.payload.meta,
        loading: false,
        isLoaded: true,
        error: {
          ...state.documentsByStatus.error,
          getDocumentsByStatus: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.getDocumentsByStatusFailure, (state, action) => {
    return {
      ...state,
      documentsByStatus: {
        ...state.documentsByStatus,
        loading: false,
        isLoaded: false,
        error: {
          ...state.documentsByStatus.error,
          getDocumentsByStatus: action.payload,
        },
      },
    };
  });
  builder.addCase(gcashActions.exportDocumentsByStatusRequest, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.exportDocumentsByStatusSuccess, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: false,
        isLoaded: true,
        error: {
          ...state.exportDocuments.error,
          exportDocument: [],
        },
      },
    };
  });
  builder.addCase(
    gcashActions.exportDocumentsByStatusFailure,
    (state, action) => {
      return {
        ...state,
        exportDocuments: {
          ...state.exportDocuments,
          loading: false,
          isLoaded: false,
          error: {
            ...state.exportDocuments.error,
            exportDocument: action.payload,
          },
        },
      };
    }
  );
  builder.addCase(gcashActions.exportSingleDocumentRequest, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.exportSingleDocumentSuccess, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: false,
        isLoaded: true,
        error: {
          ...state.exportDocuments.error,
          exportSingleDocument: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.exportSingleDocumentFailure, (state, action) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: false,
        isLoaded: false,
        error: {
          ...state.exportDocuments.error,
          exportSingleDocument: action.payload,
        },
      },
    };
  });
  builder.addCase(gcashActions.exportApplicationRequest, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: true,
      },
    };
  });
  builder.addCase(gcashActions.exportApplicationSuccess, (state) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: false,
        isLoaded: true,
        error: {
          ...state.exportDocuments.error,
          exportApplication: [],
        },
      },
    };
  });
  builder.addCase(gcashActions.exportApplicationFailure, (state, action) => {
    return {
      ...state,
      exportDocuments: {
        ...state.exportDocuments,
        loading: false,
        isLoaded: false,
        error: {
          ...state.exportDocuments.error,
          exportApplication: action.payload,
        },
      },
    };
  });
  builder.addCase(
    gcashActions.getDocumentLimitCounterStatisticsRequest,
    (state) => {
      return {
        ...state,
        limitCounterStatistics: {
          ...state.limitCounterStatistics,
          loading: true,
        },
      };
    }
  );
  builder.addCase(
    gcashActions.getDocumentLimitCounterStatisticsSuccess,
    (state, action) => {
      return {
        ...state,
        limitCounterStatistics: {
          documentCount: action.payload.documentCount,
          limit: action.payload.limit,
          loading: false,
          isLoaded: true,
          error: {
            ...state.limitCounterStatistics.error,
            getLimitCounterStatistics: [],
          },
        },
      };
    }
  );
  builder.addCase(
    gcashActions.getDocumentLimitCounterStatisticsFailure,
    (state, action) => {
      return {
        ...state,
        limitCounterStatistics: {
          ...state.limitCounterStatistics,
          loading: false,
          isLoaded: false,
          error: {
            ...state.limitCounterStatistics.error,
            getLimitCounterStatistics: action.payload,
          },
        },
      };
    }
  );
  builder.addCase(gcashActions.getGcashDocumentsStatusRequest, (state) => {
    return {
      ...state,
      documentsStatusChecking: true,
    };
  });
  builder.addCase(
    gcashActions.getGcashDocumentsStatusSuccess,
    (state, action) => {
      return {
        ...state,
        globalLoading: action.payload.isDocumentsProcessing,
        documentsStatusChecking: false,
      };
    }
  );
  builder.addCase(gcashActions.getGcashDocumentsStatusFailure, (state) => {
    return {
      ...state,
      globalLoading: false,
      documentsStatusChecking: false,
    };
  });
  builder.addCase(gcashActions.stopDocumentsStatusPolling, (state) => {
    return {
      ...state,
      globalLoading: false,
      documentsStatusChecking: false,
    };
  });
  builder.addCase(gcashActions.getGcashDocumentTypesRequest, (state) => {
    return {
      ...state,
      documentTypes: {
        ...state.documentTypes,
        loading: true,
      },
    };
  });
  builder.addCase(
    gcashActions.getGcashDocumentTypesSuccess,
    (state, action) => {
      return {
        ...state,
        documentTypes: {
          items: action.payload.items,
          loading: false,
          isLoaded: true,
          error: [],
        },
      };
    }
  );
  builder.addCase(
    gcashActions.getGcashDocumentTypesFailure,
    (state, action) => {
      return {
        ...state,
        documentTypes: {
          ...state.documentTypes,
          loading: false,
          isLoaded: false,
          error: action.payload,
        },
      };
    }
  );
});

export default gcash;
